import React from 'react';
import { BasicConfig, Utils as QbUtils } from 'react-awesome-query-builder';
import {
  ViewButton,
  EditButton,
  TestQueryButton,
} from '../Common/DataTable/tableActionButtons';
import { getLabel, getLabels } from '../Common/DataTable/tableHelpers';
import { aiRoutingModes } from '../../config/aiAgents';

const createTableColumns = (
  messageTemplates,
  customers,
  campaigns,
  leadQueryConfig,
  openViewModal,
  openEditModal,
  openTestQueryModal,
  aiAgents = [],
  aiModule = false,
) => {
  const queryBuilderConfig = { ...BasicConfig, fields: leadQueryConfig };

  const routingOf = (row) => row.aiRouting || {};

  const agentName = (row) =>
    getLabel(routingOf(row).agentId, aiAgents) ||
    routingOf(row).agentId ||
    '';

  const modeLabel = (row) => {
    const mode = aiRoutingModes.find(
      ({ value }) => value === routingOf(row).mode,
    );
    return mode ? mode.label : routingOf(row).mode || '';
  };

  const yesNo = (value, trueColor = 'success', falseColor = 'danger') =>
    value === 'yes' ? (
      <span className={`badge badge-${trueColor} font-size-12`}>YES</span>
    ) : value === 'no' ? (
      <span className={`badge badge-${falseColor} font-size-12`}>NO</span>
    ) : (
      ''
    );

  // One cell to scan, the fields below to filter on. An order routed with no
  // percentage sends nothing to the agent, so zero is shown rather than
  // nothing.
  const aiColumns = !aiModule
    ? []
    : [
        {
          title: 'AI routing',
          name: 'aiRouting',
          width: 220,
          getCellValue: (row) => {
            const routing = routingOf(row);
            if (!routing.enabled) return 'OFF';
            const parts = [modeLabel(row), `${routing.splitPercentage || 0}%`];
            if (routing.onlyWhenCallCenterClosed) parts.push('only CC closed');
            return `ON · ${parts.filter(Boolean).join(' · ')}`;
          },
          formatterComponent: ({ value, row }) =>
            value === 'OFF' ? (
              <span className="badge badge-dark font-size-12">OFF</span>
            ) : (
              <div>
                <span className="badge badge-success font-size-12">ON</span>
                <div className="text-muted font-size-12">
                  {value.replace('ON · ', '')}
                </div>
                {agentName(row) && (
                  <div className="font-size-12">{agentName(row)}</div>
                )}
              </div>
            ),
        },
        {
          title: 'AI Agent',
          name: 'aiAgentName',
          width: 150,
          getCellValue: agentName,
          hidden: true,
        },
        {
          title: 'AI Mode',
          name: 'aiMode',
          width: 120,
          getCellValue: modeLabel,
          hidden: true,
        },
        {
          title: 'AI Split',
          name: 'aiSplit',
          width: 100,
          getCellValue: (row) =>
            routingOf(row).enabled
              ? `${routingOf(row).splitPercentage || 0}%`
              : '',
          hidden: true,
        },
        {
          title: 'Only when CC closed',
          name: 'aiOnlyWhenCallCenterClosed',
          width: 120,
          getCellValue: (row) =>
            routingOf(row).enabled
              ? routingOf(row).onlyWhenCallCenterClosed
                ? 'yes'
                : 'no'
              : '',
          formatterComponent: ({ value }) => yesNo(value, 'info', 'light'),
          hidden: true,
        },
        {
          title: 'Handoff message?',
          name: 'aiHandoffMessage',
          width: 120,
          getCellValue: (row) =>
            !routingOf(row).enabled
              ? ''
              : row.aiHandoffMessage
              ? 'yes'
              : 'missing',
          formatterComponent: ({ value }) =>
            value === 'missing' ? (
              <span className="badge badge-danger font-size-12">MISSING</span>
            ) : (
              yesNo(value)
            ),
          hidden: true,
        },
        {
          title: 'Max offers',
          name: 'aiMaxOffersPerClosure',
          width: 100,
          getCellValue: (row) => row.aiMaxOffersPerClosure || 1,
          hidden: true,
        },
      ];

  return [
    {
      title: 'Name',
      name: 'name',
      width: 150,
    },
    {
      title: 'Customer',
      name: 'customerId',
      getCellValue: (row) => getLabel(row.customerId, customers),
      width: 150,
    },
    {
      title: 'Campaigns',
      name: 'campaignIds',
      getCellValue: (row) => getLabels(row.campaignIds, campaigns),
      width: 200,
    },
    {
      title: 'Active',
      name: 'active',
      width: 100,
      getCellValue: (row) => (row.active ? 'yes' : 'no'),
      formatterComponent: ({ value }) =>
        value === 'yes' ? (
          <span className="badge badge-success font-size-12">YES</span>
        ) : (
          <span className="badge badge-danger font-size-12">NO</span>
        ),
    },
    {
      title: 'Priority',
      name: 'priority',
      width: 100,
    },
    ...aiColumns,
    {
      title: 'Needs qualification?',
      name: 'qualified',
      width: 120,
      formatterComponent: ({ value }) =>
        value === 'yes' ? (
          <span className="badge badge-info font-size-12">YES</span>
        ) : (
          <span className="badge badge-dark font-size-12">NO</span>
        ),
    },
    {
      title: 'Max Call Attempts',
      name: 'maxCallAttempts',
      width: 100,
    },
    {
      title: 'Message on entry',
      name: 'messageTemplateId',
      getCellValue: (row) => getLabel(row.messageTemplateId, messageTemplates),
    },
    {
      title: 'Offers',
      name: 'monetizationOrdersAllowed',
      width: 90,
      // How many offers the agent and the operator may pick from.
      getCellValue: (row) =>
        Array.isArray(row.monetizationOrdersAllowed)
          ? row.monetizationOrdersAllowed.length
          : 0,
      hidden: true,
    },
    {
      title: 'Has query',
      name: 'query',
      width: 110,
      // Was the query builder's SQL through JSON.stringify: a string with
      // quotes and escapes, unreadable and useless to filter. The SQL itself
      // stays in the export, and readable in the view modal and the query
      // test.
      getCellValue: (row) => (row.query ? 'yes' : 'no'),
      formatterComponent: ({ value }) => yesNo(value, 'info', 'light'),
      exportValue: ({ row }) => {
        if (!row.query) return '';
        const tree = QbUtils.checkTree(
          QbUtils.loadTree(row.query),
          queryBuilderConfig,
        );
        return QbUtils.sqlFormat(tree, queryBuilderConfig) || '';
      },
      hidden: true,
    },
    {
      title: 'Cost per negative closure',
      name: 'costNegativeClosure',
      width: 120,
      formatterComponent: ({ value }) => `€ ${value}`,
      hidden: true,
    },
    {
      title: 'Sector',
      name: 'sector',
      width: 150,
      hidden: true,
    },
    {
      title: 'Phone Exchange Route',
      name: 'phoneExchangeRoute',
      width: 100,
      hidden: true,
    },
    {
      title: 'Phone Exchange Queue',
      name: 'phoneExchangeQueue',
      width: 100,
      hidden: true,
    },
    // {
    //   title: 'On Dante?',
    //   name: 'manageOnDante',
    //   width: 80,
    //   getCellValue: (row) =>
    //     !row.manageOnDante || row.manageOnDante === 'yes' ? 'yes' : 'no',
    //   formatterComponent: ({ value }) =>
    //     value === 'yes' ? (
    //       <span className="badge badge-success font-size-12">YES</span>
    //     ) : (
    //       <span className="badge badge-danger font-size-12">NO</span>
    //     ),
    //   hidden: true,
    // },
    {
      title: 'Message on call',
      name: 'callMessageTemplateIds',
      hidden: true,
      getCellValue: (row) =>
        getLabels(row.callMessageTemplateIds, messageTemplates),
    },
    {
      title: 'Action',
      name: 'action',
      width: 160,
      align: 'center',
      formatterComponent: ({ row }) => (
        <ul className="list-inline font-size-20 contact-links mb-0">
          <ViewButton row={row} onPress={openViewModal} />
          <EditButton row={row} onPress={openEditModal} />
          {row.query !== '' && (
            <TestQueryButton row={row} onPress={openTestQueryModal} />
          )}
        </ul>
      ),
      filter: 'disabled',
      sorting: 'disabled',
    },
  ];
};

export default createTableColumns;
